import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Clock, 
  Save,
  AlertTriangle, 
  XCircle, 
  CheckCircle,
  Calendar,
  User,
  MapPin,
  Timer,
  Loader2,
  Bell
} from "lucide-react";
import { useTranslation } from 'react-i18next';
import { api } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";

export default function SLATimelineModal({ isOpen, onClose, application, onUpdate }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slaData, setSlaData] = useState(null);
  const [minDays, setMinDays] = useState("");
  const [maxDays, setMaxDays] = useState("");

  useEffect(() => {
    if (isOpen && application?.id) {
      fetchSLAData();
    }
  }, [isOpen, application?.id]);

  const fetchSLAData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/applications/${application.id}/sla`);
      setSlaData(response.data);
      setMinDays(response.data.sla?.min_processing_days?.toString() || "");
      setMaxDays(response.data.sla?.max_processing_days?.toString() || "");
    } catch (error) {
      console.error('Failed to fetch SLA data:', error);
      toast.error(t('common.error') || 'Failed to load SLA data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!minDays && !maxDays) {
      toast.error(t('sla.enterDays') || 'Please enter at least one value');
      return;
    }

    const minVal = minDays ? parseInt(minDays, 10) : null;
    const maxVal = maxDays ? parseInt(maxDays, 10) : null;

    if (minVal !== null && maxVal !== null && minVal > maxVal) {
      toast.error(t('sla.minGreaterThanMax') || 'Minimum days cannot exceed maximum days');
      return;
    }

    if (minVal !== null && minVal < 1) {
      toast.error(t('sla.invalidMinDays') || 'Minimum days must be at least 1');
      return;
    }

    if (maxVal !== null && maxVal < 1) {
      toast.error(t('sla.invalidMaxDays') || 'Maximum days must be at least 1');
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams();
      if (minVal !== null) params.append('min_processing_days', minVal.toString());
      if (maxVal !== null) params.append('max_processing_days', maxVal.toString());

      const response = await api.put(`/applications/${application.id}/sla?${params.toString()}`);
      
      // Show success message with notification info
      let successMessage = t('sla.updateSuccess') || 'Deadline updated successfully';
      
      // If notifications were sent, show additional info
      if (response.data?.notifications) {
        const { assigned_official_notified, supervisor_notified, recipients } = response.data.notifications;
        if (assigned_official_notified || supervisor_notified) {
          const notifyCount = (assigned_official_notified ? 1 : 0) + (supervisor_notified ? 1 : 0);
          const recipientNames = recipients?.join(', ') || '';
          successMessage = `${successMessage}. ${t('sla.notificationsSent') || 'Notifications sent to'}: ${recipientNames || notifyCount + ' official(s)'}`;
        }
      }
      
      toast.success(successMessage);
      
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (error) {
      console.error('Failed to update SLA:', error);
      toast.error(error.response?.data?.detail || t('sla.updateError') || 'Failed to update deadline');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'ok':
        return (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
            <CheckCircle className="w-3 h-3 mr-1" />
            {t('sla.onTrack')}
          </Badge>
        );
      case 'warning':
        return (
          <Badge className="bg-amber-100 text-amber-800 border-amber-300">
            <AlertTriangle className="w-3 h-3 mr-1" />
            {t('sla.warningShort')}
          </Badge>
        );
      case 'violated':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-300">
            <XCircle className="w-3 h-3 mr-1" />
            {t('sla.violated')}
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {t('sla.noSla')}
          </Badge>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[70vh] sm:max-h-[85vh] flex flex-col p-0" data-testid="sla-timeline-modal">
        <div className="flex-shrink-0 px-4 pt-4 pb-1 sm:px-6 sm:pt-6 sm:pb-2">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Timer className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700" />
              <span className="text-sm sm:text-lg">{t('sla.editTimeline') || 'Edit Timeline'}</span>
            </DialogTitle>
            <DialogDescription className="hidden sm:block">
              {t('sla.editTimelineDesc') || 'Update the processing deadline for this application'}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          ) : slaData ? (
            <div className="space-y-3 sm:space-y-6 pb-1">
              {/* Compact App Info - single line on mobile, card on desktop */}
              <div className="text-xs text-slate-600 space-y-0.5 sm:bg-stone-50 sm:rounded-lg sm:p-4 sm:space-y-3 sm:border sm:border-stone-200 sm:text-sm">
                <p className="truncate"><MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600 inline mr-1" /><span className="font-medium">{t('table.surveyNumber')}:</span> {slaData.survey_number || application?.land_survey_number}</p>
                <p className="truncate"><User className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600 inline mr-1" /><span className="font-medium">{t('table.farmer')}:</span> {slaData.farmer_name || application?.farmer_name}</p>
              </div>

              {/* Compact SLA Status - inline row on mobile, 2x2 grid on desktop */}
              <div className="flex items-center gap-2 sm:hidden">
                {getStatusBadge(slaData.sla_status?.status)}
                <span className="text-xs text-slate-500">|</span>
                <span className="text-xs text-slate-600"><Calendar className="w-3 h-3 inline mr-0.5" />{formatDate(slaData.sla?.deadline)}</span>
                <span className="text-xs text-slate-500">|</span>
                <span className={`text-xs font-bold ${
                  slaData.sla_status?.status === 'violated' ? 'text-red-600' : 
                  slaData.sla_status?.status === 'warning' ? 'text-amber-600' : 'text-emerald-600'
                }`}>
                  {slaData.sla_status?.status === 'violated' 
                    ? `${slaData.sla_status?.days_overdue || 0} ${t('sla.daysOverdue') || 'overdue'}`
                    : `${slaData.sla_status?.days_remaining || 0} ${t('sla.daysRemaining') || 'remaining'}`
                  }
                </span>
              </div>
              {/* Desktop 2x2 grid */}
              <div className="hidden sm:grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-3 border border-stone-200">
                  <p className="text-xs text-slate-500 mb-1">{t('sla.status')}</p>
                  {getStatusBadge(slaData.sla_status?.status)}
                </div>
                <div className="bg-white rounded-lg p-3 border border-stone-200">
                  <p className="text-xs text-slate-500 mb-1">{t('sla.deadline')}</p>
                  <p className="text-sm font-medium flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {formatDate(slaData.sla?.deadline)}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-stone-200">
                  <p className="text-xs text-slate-500 mb-1">{t('sla.daysElapsed')}</p>
                  <p className="text-lg font-bold text-slate-700">{slaData.sla_status?.days_elapsed || 0}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-stone-200">
                  <p className="text-xs text-slate-500 mb-1">
                    {slaData.sla_status?.status === 'violated' ? t('sla.daysOverdue') : t('sla.daysRemaining')}
                  </p>
                  <p className={`text-lg font-bold ${
                    slaData.sla_status?.status === 'violated' ? 'text-red-600' : 
                    slaData.sla_status?.status === 'warning' ? 'text-amber-600' : 'text-emerald-600'
                  }`}>
                    {slaData.sla_status?.status === 'violated' 
                      ? slaData.sla_status?.days_overdue || 0
                      : slaData.sla_status?.days_remaining || 0
                    }
                  </p>
                </div>
              </div>

              {/* Edit Form */}
              <div className="border-t border-stone-200 pt-2.5 sm:pt-4">
                <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="minDays" className="text-xs">{t('sla.minDays')}</Label>
                    <Input
                      id="minDays"
                      type="number"
                      min="1"
                      max="365"
                      value={minDays}
                      onChange={(e) => setMinDays(e.target.value)}
                      placeholder={slaData.sla?.min_processing_days?.toString() || "3"}
                      className="w-full h-8 sm:h-10 text-sm"
                      data-testid="sla-min-days-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="maxDays" className="text-xs">{t('sla.maxDays')}</Label>
                    <Input
                      id="maxDays"
                      type="number"
                      min="1"
                      max="365"
                      value={maxDays}
                      onChange={(e) => setMaxDays(e.target.value)}
                      placeholder={slaData.sla?.max_processing_days?.toString() || "7"}
                      className="w-full h-8 sm:h-10 text-sm"
                      data-testid="sla-max-days-input"
                    />
                  </div>
                </div>
                
                {/* Compact note */}
                <p className="mt-2 text-[10px] sm:text-xs text-amber-700">
                  <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3 inline mr-0.5" />
                  {t('sla.updateNote') || 'Updating max days recalculates the deadline from assignment date.'}
                </p>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex-shrink-0 border-t border-stone-200 px-4 py-2.5 sm:p-6 sm:pt-4 bg-background">
          <DialogFooter className="flex-row justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={saving}
              className="h-9 sm:h-10"
              data-testid="sla-modal-cancel"
            >
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || loading}
              className="bg-emerald-600 hover:bg-emerald-700 h-9 sm:h-10"
              data-testid="sla-modal-save"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1.5" />
              )}
              {t('common.save') || 'Save'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
